import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Send, Sparkles, Bot, User, RefreshCw, Save,
  Upload, X, FileText, Image, File
} from 'lucide-react';

import { authHeaders } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ACCEPTED = '.jpg,.jpeg,.png,.webp,.pdf,.docx,.txt';

// Icône selon type de fichier
function FileIcon({ type }) {
  if (type?.startsWith('image/')) return <Image size={14} style={{ color: 'var(--admin-teal)' }} />;
  if (type === 'application/pdf') return <FileText size={14} style={{ color: '#FF5C6C' }} />;
  return <File size={14} style={{ color: 'var(--admin-gold)' }} />;
}

// Convertit fichier en base64 ou texte selon le type
async function extractFileContent(file) {
  if (file.type.startsWith('image/')) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        resolve({ type: 'image', content: base64, filename: file.name });
      };
      reader.readAsDataURL(file);
    });
  }

  if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        resolve({ type: 'pdf', content: base64, filename: file.name, isBase64: true });
      };
      reader.readAsDataURL(file);
    });
  }

  // Texte (docx, txt)
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve({ type: 'text', content: e.target.result, filename: file.name });
    reader.readAsText(file);
  });
}

// ═══ Composants extraits du rendu (évite le remount à chaque render) ═══

/**
 * Fiche produit générée par l'IA — doit être défini HORS du composant parent.
 * Reçoit les callbacks du parent en props.
 */
function ProductCard({ product, onSave, saving }) {
  return (
    <div className="w-full p-4 rounded-2xl mt-2"
      style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid var(--admin-teal-line)' }}>
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Parametres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Agent IA Catalogue</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Dialogue avec agent IA</p>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold" style={{ color: 'var(--admin-teal)' }}>Fiche générée</p>
        <div className="flex gap-2">
          <button onClick={onSave} disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold disabled:opacity-40"
            style={{ background: '#D4AF37', color: '#0D0D0D' }}>
            <Save size={12} />
            {saving ? 'Création...' : 'Créer le produit'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5 text-xs">
        {[
          ['Nom', product.nom],
          ['Type', product.type],
          ['Univers', (product.univers || []).join(', ')],
          ['Tarif public', `${product.tarifs_suggestion?.particulier || 0}€`],
          ['Tags', (product.tags || []).slice(0, 3).join(', ')],
          ['SEO', product.seo_title?.slice(0, 40) + '...'],
        ].map(([k, v], i) => v && (
          <div key={`${k}-${i}`}>
            <span style={{ color: 'var(--admin-faint)' }}>{k}: </span>
            <span style={{ color: 'var(--admin-text)' }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Bulle de message dans le chat IA — défini HORS du composant parent.
 * Reçoit les callbacks en props.
 */
function MessageBubble({ msg, onSaveProduct, saving }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: isUser ? 'rgba(212,175,55,0.15)' : 'rgba(93,188,191,0.15)' }}>
        {isUser
          ? <User size={15} style={{ color: 'var(--admin-gold)' }} />
          : <Bot size={15} style={{ color: 'var(--admin-teal)' }} />}
      </div>
      <div className={`max-w-2xl ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1.5`}>
        {msg.files?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {msg.files.map((f, fi) => (
              <span key={`${f.name}-${fi}`} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs"
                style={{ background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)' }}>
                <FileIcon type={f.type} /> {f.name}
              </span>
            ))}
          </div>
        )}
        <div className="px-4 py-3 rounded-2xl text-sm"
          style={{
            background: isUser ? 'rgba(212,175,55,0.12)' : '#161616',
            border: `1px solid ${isUser ? 'rgba(212,175,55,0.2)' : '#2A2A2A'}`,
            color: 'var(--admin-text)', lineHeight: '1.65', whiteSpace: 'pre-wrap'
          }}>
          {msg.content}
        </div>
        {msg.product && (
          <ProductCard product={msg.product} onSave={onSaveProduct} saving={saving} />
        )}
      </div>
    </div>
  );
}

export default function AdminIAAgent() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Bonjour ! Je suis votre agent IA catalogue Show Événement. 🎪

**Je peux analyser :**
📸 **Images** — photo d'un produit → fiche complète
📄 **PDF / Documents** — fiche technique → caractéristiques
💬 **Texte** — description libre → fiche optimisée
🔗 **Tout en même temps** — image + texte + doc → fiche parfaite

**Pour commencer :**
- Glissez un fichier ou cliquez sur 📎
- Ou décrivez simplement votre produit

*Exemple : "Château gonflable licorne, 6x6m, enfants 3-12 ans, 180€"*`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [lastProduct, setLastProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [improveMode, setImproveMode] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    fetch(`${BACKEND}/api/products?limit=50`)
      .then(r => r.json())
      .then(d => setProducts(d.products || []))
      .catch((err) => { /* eslint-disable-line no-empty */ });
  }, []);

  // Drag & Drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  }, []);

  function addFiles(files) {
    const valid = files.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return ['jpg','jpeg','png','webp','pdf','docx','txt'].includes(ext);
    });
    if (valid.length !== files.length) {
      toast.warning('Formats acceptés: JPG, PNG, WEBP, PDF, DOCX, TXT');
    }
    setAttachedFiles(prev => [...prev, ...valid.slice(0, 3 - prev.length)]);
  }

  function removeFile(i) {
    setAttachedFiles(prev => prev.filter((_, idx) => idx !== i));
  }

  async function sendMessage() {
    if ((!input.trim() && attachedFiles.length === 0) || loading) return;

    const userMsg = input.trim();
    setInput('');
    const files = [...attachedFiles];
    setAttachedFiles([]);
    setLoading(true);

    // Afficher le message utilisateur
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMsg || `📎 ${files.map(f => f.name).join(', ')}`,
      files: files.map(f => ({ name: f.name, type: f.type }))
    }]);

    try {
      let response, productData;

      if (files.length > 0) {
        // Analyser le premier fichier (le plus important)
        const fileData = await extractFileContent(files[0]);

        const payload = {
          file_type: fileData.type,
          content: fileData.content,
          filename: fileData.filename,
          extra_info: userMsg || ''
        };

        // Si image avec base64
        if (fileData.type === 'image') payload.file_type = 'image';
        else if (fileData.isBase64) {
          // PDF en base64 — extraire texte côté serveur
          payload.file_type = 'pdf';
        }

        const res = await fetch(`${BACKEND}/api/ia/analyse-fichier`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        response = data.response;
        productData = data.product;

      } else {
        // Texte seul — agent catalogue classique
        const res = await fetch(`${BACKEND}/api/ia/agent-catalogue`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ message: userMsg, history: messages })
        });
        const data = await res.json();
        response = data.response;
        productData = data.product;
      }

      if (productData) setLastProduct(productData);

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response || 'Analyse effectuée.',
        product: productData
      }]);

    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Erreur de connexion. Vérifiez que l\'IA est configurée.'
      }]);
    }
    setLoading(false);
  }

  async function saveProduct() {
    if (!lastProduct) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: lastProduct.nom || 'Nouveau produit',
          description: lastProduct.description || '',
          description_longue: lastProduct.description_longue || '',
          type: lastProduct.type || 'location',
          univers: lastProduct.univers || [],
          tags: lastProduct.tags || [],
          seo_title: lastProduct.seo_title || '',
          seo_description: lastProduct.seo_description || '',
          faq: lastProduct.faq || [],
          tarifs: lastProduct.tarifs_suggestion || { particulier: 0, entreprise: 0, association: 0, ce: 0, groupe: 0 },
          images: [],
          disponible: true,
          featured: false,
          order_weight: 50
        })
      });
      if (res.ok) {
        const created = await res.json();
        toast.success(`Produit "${created.nom}" créé !`, { description: 'Ajoutez la photo dans le Catalogue' });
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✅ **Produit créé !** Rendez-vous dans **Admin → Catalogue** pour ajouter la photo.\n\nVoulez-vous créer un autre produit ?`
        }]);
        setLastProduct(null);
      }
    } catch (e) { toast.error('Erreur réseau'); }
    setSaving(false);
  }

  async function improveProduct() {
    if (!selectedProductId) return;
    const p = products.find(x => x.id === selectedProductId);
    if (!p) return;
    const prompt = `Améliore et optimise cette fiche produit pour Show Événement:\n\nNom: ${p.nom}\nDescription: ${p.description || ''}\nType: ${p.type}\nUnivers: ${(p.univers||[]).join(', ')}\nTags: ${(p.tags||[]).join(', ')}\n\nGénère une version améliorée avec meilleur titre, description plus vendeuse, SEO optimisé, FAQ enrichie.`;
    setImproveMode(false);
    setInput(prompt);
    setTimeout(() => sendMessage(), 100);
  }

  function resetChat() {
    setMessages([{ role: 'assistant', content: 'Chat réinitialisé. Que souhaitez-vous créer ?' }]);
    setLastProduct(null);
    setAttachedFiles([]);
    setInput('');
  }

  // ── Product Card et MessageBubble sont maintenant définis HORS du composant (voir haut du fichier)

  const s = { background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' };

  return (
    <div style={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2" style={{ color: 'var(--admin-text)' }}>
            <Sparkles size={20} style={{ color: 'var(--admin-gold)' }} /> Agent IA Catalogue
          </h2>
          <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
            Image · PDF · Doc · Texte → Fiche produit complète
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setImproveMode(!improveMode)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs"
            style={{ background: improveMode ? 'rgba(212,175,55,0.15)' : '#161616', color: improveMode ? '#D4AF37' : 'rgba(255,255,255,0.4)', border: `1px solid ${improveMode ? 'rgba(212,175,55,0.3)' : '#2A2A2A'}` }}>
            <Sparkles size={13} /> Améliorer
          </button>
          <button onClick={resetChat}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs"
            style={{ background: 'var(--admin-surface)', color: 'var(--admin-muted)', border: '1px solid #2A2A2A' }}>
            <RefreshCw size={13} /> Réinitialiser
          </button>
        </div>
      </div>

      {/* Mode amélioration */}
      {improveMode && (
        <div className="flex gap-2 mb-3 p-3 rounded-xl flex-shrink-0"
          style={{ background: 'var(--admin-gold-dim)', border: '1px solid rgba(212,175,55,0.2)' }}>
          <select value={selectedProductId} onChange={e => setSelectedProductId(e.target.value)}
            className="flex-1 px-3 py-2 rounded-xl text-sm" style={s}>
            <option value="">Sélectionner un produit à optimiser...</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
          </select>
          <button onClick={improveProduct} disabled={!selectedProductId}
            className="px-4 py-2 rounded-xl text-xs font-bold disabled:opacity-40"
            style={{ background: '#D4AF37', color: '#0D0D0D' }}>
            Optimiser
          </button>
        </div>
      )}

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto rounded-xl p-4 mb-3"
        style={{ background: 'var(--admin-bg)', border: `2px dashed ${isDragOver ? '#D4AF37' : '#2A2A2A'}`, transition: 'border-color 0.2s' }}
        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl"
            style={{ background: 'var(--admin-gold-dim)', border: '2px dashed #D4AF37', zIndex: 10 }}>
            <p className="font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>📎 Déposer le fichier ici</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <MessageBubble key={`msg-${i}-${msg.role}`} msg={msg} onSaveProduct={saveProduct} saving={saving} />
        ))}
        {loading && (
          <div className="flex gap-3 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(93,188,191,0.15)' }}>
              <Bot size={15} style={{ color: 'var(--admin-teal)' }} />
            </div>
            <div className="px-4 py-3 rounded-2xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
              <div className="flex gap-1">
                {[0,1,2].map(i => (
                  <div key={`list-${i}`} className="w-2 h-2 rounded-full animate-pulse"
                    style={{ background: '#5DBCBF', animationDelay: `${i*0.2}s` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Fichiers joints */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2 flex-shrink-0">
          {attachedFiles.map((f, i) => (
            <div key={`list-${i}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs"
              style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }}>
              <FileIcon type={f.type} />
              <span className="max-w-32 truncate">{f.name}</span>
              <span className="text-xs" style={{ color: 'var(--admin-faint)' }}>
                ({(f.size / 1024).toFixed(0)}KB)
              </span>
              <button onClick={() => removeFile(i)} style={{ color: '#FF5C6C' }}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2 flex-shrink-0">
        {/* Bouton upload fichier */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-10 h-auto rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
          style={{ background: 'var(--admin-surface)', color: attachedFiles.length > 0 ? '#D4AF37' : 'rgba(255,255,255,0.4)', border: '1px solid #2A2A2A' }}
          title="Ajouter un fichier (image, PDF, document)">
          <Upload size={18} />
        </button>
        <input ref={fileInputRef} type="file" accept={ACCEPTED} multiple className="hidden"
          onChange={e => addFiles(Array.from(e.target.files))} />

        <textarea
          ref={textareaRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder={attachedFiles.length > 0 ? "Ajouter des infos sur ce fichier... (optionnel)" : "Décrire un produit ou glisser un fichier..."}
          rows={2}
          className="flex-1 px-4 py-3 rounded-xl text-sm resize-none"
          style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A', color: 'var(--admin-text)', lineHeight: '1.5' }}
        />
        <button id="send-btn-ia" onClick={sendMessage}
          disabled={loading || (!input.trim() && attachedFiles.length === 0)}
          className="w-12 h-auto rounded-xl flex items-center justify-center disabled:opacity-40"
          style={{ background: '#D4AF37', color: '#0D0D0D' }}>
          <Send size={18} />
        </button>
      </div>

      <p className="text-xs text-center mt-1.5 flex-shrink-0" style={{ color: 'var(--admin-faint)' }}>
        📎 Glissez un fichier sur la zone de chat · Formats: JPG, PNG, WEBP, PDF, DOCX
      </p>
    </div>
  );
}
